import { useTranslation } from "react-i18next";

import { viewportSectionClasses } from "@/components/marketing/Section";
import { WaitlistForm } from "@/components/marketing/WaitlistForm";
import { AnimatedContainer } from "@/components/ui/motion";
import WaveText from "@/components/ui/wave-text";
import { BRAND_NAME } from "@/lib/brand";
import { cn } from "@/lib/cn";

// Closing CTA and the canonical waitlist form. `id="waitlist"` is the scroll
// target every "Join the waitlist" link on the site points at.
export function FinalCta() {
  const { t } = useTranslation();

  return (
    <section
      id="waitlist"
      className={cn("relative overflow-hidden py-16 lg:py-20", viewportSectionClasses)}
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-radial-glow opacity-80" />

      {/* Grain for the watermark below: turbulence masked to the glyph alpha,
          blended back over the gradient as film noise. */}
      <svg aria-hidden="true" className="absolute h-0 w-0">
        <filter id="wm-noise">
          <feTurbulence type="fractalNoise" baseFrequency="0.5" numOctaves="2" result="noise" />
          <feComponentTransfer in="noise" result="opacityNoise">
            <feFuncA type="linear" slope="0.075" />
          </feComponentTransfer>
          <feComposite in="opacityNoise" in2="SourceGraphic" operator="in" result="maskedNoise" />
          <feBlend in="SourceGraphic" in2="maskedNoise" mode="overlay" />
        </filter>
      </svg>

      {/* Giant brand watermark — dark→lime gradient sweep clipped to the
          glyphs. The CTA stack overlaps its lower third, so the word reads as
          a horizon line rising behind the closing ask. */}
      <AnimatedContainer className="relative z-0 -mb-[0.3em] px-2 pt-2 md:-mb-[0.48em]" delay={0.1}>
        <div
          aria-hidden
          className="watermark-text pointer-events-none select-none whitespace-nowrap text-center text-[clamp(5rem,17vw,15rem)] font-extrabold leading-[0.9] tracking-[-0.05em]"
        >
          {BRAND_NAME}
        </div>
      </AnimatedContainer>

      <AnimatedContainer className="relative z-[1] mx-auto flex max-w-3xl flex-col items-center px-5 text-center sm:px-6">
        {/* Mirrors the hero stack: pulsing-dot status eyebrow over the headline. */}
        <span className="mb-5 inline-flex w-fit items-center gap-2 font-sans text-eyebrow font-medium uppercase text-primary">
          <span
            aria-hidden
            className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-primary"
          />
          {t("finalCta.eyebrow")}
        </span>

        {/* Setup → punchline: the dim first line tees up the bright second,
            with the brand name carrying the lime accent (same as the hero). */}
        <h2 className="font-sans text-display font-semibold tracking-tight">
          <span className="block text-foreground/55">
            <WaveText text={t("finalCta.titleLead")} />
          </span>
          <span className="block text-foreground">
            <WaveText text={t("finalCta.titlePunch")} delay={0.55} />{" "}
            <span className="text-primary">
              <WaveText text={t("finalCta.titleAccent")} delay={0.85} />
            </span>
          </span>
        </h2>
        <WaitlistForm className="mt-9" />
      </AnimatedContainer>
    </section>
  );
}
